import Back from 'components/Back';
import {
  Button, Card, Container, ErrorMessage, Form, Select, TextInput, Textarea,
} from 'nhsuk-react-components';
import { ChangeEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import useFetchCampaignTemplatesForTeam, { ICampaignTemplate } from 'queries/fetch/fetchCampaignTemplatesForTeam.ts';
import useCreateCampaignTemplate from 'queries/mutate/createCampaignTemplate.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import useSetTeamTemplates from 'queries/mutate/setTeamTemplates.ts';
import useUpdateCampaignTemplate from 'queries/mutate/updateCampaignTemplate.ts';
import {
  AllowedTemplateKeys,
  TEMPLATE_PROPERTY_KEYS,
  TemplateProperty,
  TODAY_DATE_TOKEN,
} from 'schemas/CampaignTemplates.schema.ts';
import { stripRoutingPlanVersions } from 'utils/routingPlansUtils.ts';

const adjustProperties = (properties: TemplateProperty[]): TemplateProperty[] => properties.map((property) => {
  if (property.key === 'CampaignConfig.AvailableRoutingIds.input.default') {
    return {
      ...property,
      value: stripRoutingPlanVersions(property.value),
    };
  }

  return property;
});

const SUGGESTED_VALUES: Record<AllowedTemplateKeys, string> = {
  'CampaignConfig.Type.input.hint': 'e.g. Choose Vaccination or Awareness',
  'CampaignConfig.Type.input.default': 'e.g. \'V\' (vaccination) or \'A\' (awareness)',
  'CampaignConfig.Type.input.display': 'e.g. hidden',
  'CampaignConfig.Type.summary.display': 'e.g. hidden',
  'CampaignConfig.Target.input.default': 'e.g. COVID, TALKINGTHERAPIES',
  'CampaignConfig.Target.input.display': 'e.g. hidden',
  'CampaignConfig.Target.summary.display': 'e.g. hidden',
  'CampaignConfig.Name.input.default': 'e.g. Talking Therapies Campaign',
  'CampaignConfig.StartDate.input.default': `e.g. 20250325 or ${TODAY_DATE_TOKEN} to default to today's date`,
  'CampaignConfig.StartDate.input.display': 'e.g. hidden',
  'CampaignConfig.StartDate.summary.display': 'e.g. hidden',
  'CampaignConfig.StartDate.header.display': 'e.g. hidden',
  'CampaignConfig.EndDate.input.default': 'e.g. 20250617',
  'CampaignConfig.EndDate.input.display': 'e.g. hidden',
  'CampaignConfig.EndDate.summary.display': 'e.g. hidden',
  'CampaignConfig.EndDate.header.display': 'e.g. hidden',
  'CampaignConfig.Approver.input.default': 'e.g. your.name1@nhs.net,another.name1@nhs.net',
  'CampaignConfig.Approver.input.display': 'e.g. hidden',
  'CampaignConfig.Approver.summary.display': 'e.g. hidden',
  'CampaignConfig.Manager.input.default': 'e.g. your.name1@nhs.net,another.name1@nhs.net',
  'CampaignConfig.Manager.input.display': 'e.g. hidden',
  'CampaignConfig.Manager.summary.display': 'e.g. hidden',
  'CampaignConfig.Reviewer.input.default': 'e.g. your.name1@nhs.net,another.name1@nhs.net',
  'CampaignConfig.Reviewer.input.display': 'e.g. hidden',
  'CampaignConfig.Reviewer.summary.display': 'e.g. hidden',
  'CampaignConfig.AvailableRoutingIds.input.default': 'Comma separated plan IDs (without version), e.g. LEGACY_PLAN_ID, 45f8f741-db7b-42c8-bc82-95c0bee59faa. Only routing plans for the current team are returned (with the latest version number), however when creating or updating you should use the complete list (without versions).',
  'CampaignConfig.AvailableRoutingIds.input.display': 'e.g. hidden',
  'IterationDetails.IterationCohorts.input.default': 'Comma separated cohort labels, in order of priority.',
  'IterationDetails.IterationCohorts.input.display': 'e.g. hidden',
  'IterationDetails.IterationRules.filters.input.default': 'JSON array of IterationRules',
  'IterationDetails.IterationRules.filters.input.display': 'e.g. hidden',
  'IterationDetails.IterationRules.suppressions.input.default': 'JSON array of IterationRules',
  'IterationDetails.IterationRules.suppressions.input.display': 'e.g. hidden',
  'IterationDetails.IterationRules.routing.input.default': 'JSON array of IterationRules',
  'IterationDetails.IterationRules.routing.input.display': 'e.g. hidden',
};

interface IPropertiesEditorProps {
  properties: TemplateProperty[];
  onChange: (properties: TemplateProperty[]) => void;
}

function PropertiesEditor({ properties, onChange }: IPropertiesEditorProps) {
  return (
    <div>
      {properties.map((property, index) => (
        <div
          key={property.key}
          style={{
            border: '1px solid #d8dde0',
            borderRadius: '4px',
            padding: '16px 16px 0',
            marginBottom: '8px',
          }}
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
            <div style={{ flex: 4 }}>
              <Select
                label="Key"
                style={{ width: '100%' }}
                value={property.key}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  onChange(
                    properties.map((p, i) => (
                      i === index ? {
                        ...p,
                        key: e.target.value as AllowedTemplateKeys,
                      }
                        : p
                    )),
                  );
                }}
              >
                {TEMPLATE_PROPERTY_KEYS.filter((key) => key === property.key || !properties.some((p, i) => i !== index && p.key === key)).map((key) => (
                  <Select.Option key={key} value={key}>{key}</Select.Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 2 }}>
              <TextInput
                label="Value"
                value={property.value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onChange(properties.map((p, i) => (i === index ? { ...p, value: e.target.value } : p)));
                }}
              />
            </div>
            <div>
              <Button
                warning
                onClick={(e) => {
                  e.preventDefault();
                  onChange(properties.filter((_, i) => i !== index));
                }}
              >
                Remove
              </Button>
            </div>
          </div>
          {SUGGESTED_VALUES[property.key] && (
            <p className="nhsuk-hint">{SUGGESTED_VALUES[property.key]}</p>
          )}
        </div>
      ))}
      <Button
        secondary
        className="nhsuk-u-margin-top-3"
        disabled={properties.length >= TEMPLATE_PROPERTY_KEYS.length}
        onClick={(e) => {
          e.preventDefault();
          const nextKey = TEMPLATE_PROPERTY_KEYS.find((key) => !properties.some((p) => p.key === key));
          if (nextKey) {
            onChange([...properties, { key: nextKey, value: '' }]);
          }
        }}
      >
        Add property
      </Button>
    </div>
  );
}

function CampaignTemplatesPage() {
  const { teamId } = useParams() as { teamId: string };
  const templatesForTeam = useFetchCampaignTemplatesForTeam({ teamId });

  const createCampaignTemplate = useCreateCampaignTemplate();
  const [createTemplateError, setCreateTemplateError] = useState<Error | null>(null);
  const [createName, setCreateName] = useState('');
  const [createProperties, setCreateProperties] = useState<TemplateProperty[]>([]);

  const setTeamTemplates = useSetTeamTemplates();
  const [setTeamTemplatesError, setSetTeamTemplatesError] = useState<Error | null>(null);

  const updateCampaignTemplate = useUpdateCampaignTemplate();
  const [updateCampaignTemplateError, setUpdateCampaignTemplateError] = useState<Error | null>(null);
  const [updateTemplateId, setUpdateTemplateId] = useState('');
  const [updateName, setUpdateName] = useState('');
  const [updateProperties, setUpdateProperties] = useState<TemplateProperty[]>([]);

  if (!templatesForTeam.data) {
    return <ProgressIndicator />;
  }

  const handleUpdateTemplateChange = (id: string) => {
    const template = templatesForTeam.data?.templates.find((t) => t.id === id);
    setUpdateTemplateId(id);
    setUpdateName(template?.name ?? '');
    setUpdateProperties(template?.properties ?? []);
  };

  return (
    <Container>
      <div className="nhsuk-u-margin-top-6 nhsuk-u-margin-bottom-6">
        <Back backTo="/admin" />
      </div>
      <Card>
        <h1
          role="main"
          id="maincontent"
          className="nhsuk-heading-l nhsuk-u-margin-top-0 nhsuk-u-margin-bottom-3"
        >
          Manage campaign templates
        </h1>
      </Card>
      <Card>
        <h3>Templates for team:</h3>
        <pre style={{ overflowX: 'auto' }}>{JSON.stringify(templatesForTeam.data, null, 4)}</pre>
      </Card>

      <Card>
        <Form onSubmit={async (event) => {
          try {
            setCreateTemplateError(null);
            event.preventDefault();
            await createCampaignTemplate.mutateAsync({
              teamId,
              template: {
                name: createName,
                properties: adjustProperties(createProperties),
              } as ICampaignTemplate,
            }, {});
          } catch (error) {
            if (typeof error === 'string') {
              setCreateTemplateError(new Error(error));
              return;
            }
            setCreateTemplateError(error as Error);
          }
        }}
        >
          <h3>Add new template:</h3>
          <TextInput
            label="Template name"
            value={createName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCreateName(e.target.value)}
          />
          <h4>Properties:</h4>
          <PropertiesEditor properties={createProperties} onChange={setCreateProperties} />
          <div className="nhsuk-u-margin-top-4" style={{ borderTop: '1px solid #d8dde0', paddingTop: '24px' }}>
            <Button>Create</Button>
          </div>
          {createCampaignTemplate.data && (
            <>
              <h4>Success!</h4>
              <pre>
                {' '}
                {JSON.stringify(createCampaignTemplate.data, null, 4)}
                {' '}
              </pre>
              <Button
                disabled={templatesForTeam.data.templates.some((t) => t.id === createCampaignTemplate.data?.templateId)}
                onClick={async (event) => {
                  event.preventDefault();
                  await setTeamTemplates.mutateAsync({
                    teamId,
                    template_ids: [...templatesForTeam.data.templates.map((t) => t.id), createCampaignTemplate.data?.templateId],
                  });
                }}
              >
                Add to current team
              </Button>
            </>
          )}
          {createTemplateError && <ErrorMessage>{createTemplateError.message}</ErrorMessage>}
        </Form>
      </Card>

      <Card>
        <Form onSubmit={async (event) => {
          try {
            setSetTeamTemplatesError(null);
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            await setTeamTemplates.mutateAsync({
              teamId,
              template_ids: JSON.parse(formData.get('team-templates-json') as string).template_ids as string[],
            }, {});
          } catch (error) {
            if (typeof error === 'string') {
              setSetTeamTemplatesError(new Error(error));
              return;
            }
            setSetTeamTemplatesError(error as Error);
          }
        }}
        >
          <h3>Set team templates:</h3>
          <Textarea
            label="Team Templates JSON"
            name="team-templates-json"
            rows={5}
            defaultValue={JSON.stringify({ template_ids: templatesForTeam.data.templates.map((t) => t.id) }, null, 4)}
          />
          <Button>Set</Button>
          {setTeamTemplates.data && (
            <>
              <h4>Success!</h4>
              <pre>
                {' '}
                {JSON.stringify(setTeamTemplates.data, null, 4)}
                {' '}
              </pre>
            </>
          )}
          {setTeamTemplatesError && <ErrorMessage>{setTeamTemplatesError.message}</ErrorMessage>}
        </Form>
      </Card>

      <Card>
        <Form onSubmit={async (event) => {
          try {
            setUpdateCampaignTemplateError(null);
            event.preventDefault();
            await updateCampaignTemplate.mutateAsync({
              teamId,
              templateID: updateTemplateId,
              template: {
                name: updateName,
                properties: adjustProperties(updateProperties),
              } as ICampaignTemplate,
            }, {});
          } catch (error) {
            if (typeof error === 'string') {
              setUpdateCampaignTemplateError(new Error(error));
              return;
            }
            setUpdateCampaignTemplateError(error as Error);
          }
        }}
        >
          <h3>Update template:</h3>
          <Select
            label="Choose template"
            name="template-id"
            value={updateTemplateId}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => handleUpdateTemplateChange(e.target.value)}
          >
            <Select.Option value="">Select a template</Select.Option>
            {templatesForTeam.data.templates.map((template) => (
              <Select.Option key={template.id} value={template.id}>
                {template.name}
                {' '}
                (
                {template.version}
                )
              </Select.Option>
            ))}
          </Select>
          <TextInput
            label="Template name"
            value={updateName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setUpdateName(e.target.value)}
          />
          <h4>Properties:</h4>
          <PropertiesEditor properties={updateProperties} onChange={setUpdateProperties} />
          <div className="nhsuk-u-margin-top-4" style={{ borderTop: '1px solid #d8dde0', paddingTop: '24px' }}>
            <Button>Set</Button>
          </div>
          {updateCampaignTemplate.data && (
            <>
              <h4>Success!</h4>
              <pre>
                {' '}
                {JSON.stringify(updateCampaignTemplate.data, null, 4)}
                {' '}
              </pre>
            </>
          )}
          {updateCampaignTemplateError && <ErrorMessage>{updateCampaignTemplateError.message}</ErrorMessage>}
        </Form>
      </Card>
    </Container>
  );
}

export default CampaignTemplatesPage;
